import React from 'react';
import Text from '../Text/Text';
import './TransactionItem.css';
import LabeledValue from '../LabeledValue';
import { Transaction } from '../../types';
import { getTrackingStatus } from '../../utils/timeCalculations';
import { DisputeStatus } from '../../types/dispute';

// Generic avatar image (purple user icon)
const GENERIC_AVATAR = 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHBhdGggZmlsbD0iIzhhMmJlMiIgZD0iTTEyIDJDNi40OCAyIDIgNi40OCAyIDEyczQuNDggMTAgMTAgMTAgMTAtNC40OCAxMC0xMFMxNy41MiAyIDEyIDJ6bTAgM2MxLjY2IDAgMyAxLjM0IDMgM3MtMS4zNCAzLTMgMy0zLTEuMzQtMy0zIDEuMzQtMyAzLTN6bTAgMTQuMmMtMi41IDAtNC43MS0xLjI4LTYtMy4yMi4wMy0xLjk5IDQtMy4wOCA2LTMuMDggMS45OSAwIDUuOTcgMS4wOSA2IDMuMDgtMS4yOSAxLjk0LTMuNSAzLjIyLTYgMy4yMnoiLz48L3N2Zz4=';

interface TransactionItemProps {
  avatar?: string; // Make avatar optional
  role: string;
  itemDescription: string; // Added to support item description
  itemPrice?: string; // Added to support item price,
  itemBuyer?: string,
  itemSeller?: string,
  status: React.ReactNode; // Changed from string to ReactNode to support styled text
  action?: React.ReactNode; // Added to support action buttons
  onClick?: () => void;
  onOpenDispute?: (transaction: Transaction, e: React.MouseEvent) => void; // Added to support dispute functionality
  priceTagStyle?: 'none' | 'ribbon-left' | 'ribbon-right' | 'tag-left' | 'tag-right'; // Added to support price tag styles
  transaction?: Transaction; // Added to support tracking time remaining indicator
}

const TransactionItem: React.FC<TransactionItemProps> = ({
  avatar = GENERIC_AVATAR, // Use generic avatar as default
  role,
  itemDescription,
  itemPrice,
  itemBuyer,
  itemSeller,
  status,
  action,
  onClick,
  onOpenDispute,
  priceTagStyle = 'none', // Default to 'none' to maintain backward compatibility
  transaction
}) => {
  // Function to render tracking time remaining indicator
  const renderTrackingTimeRemaining = () => {
    if (!transaction ||
        transaction.status !== 'in_escrow' ||
        transaction.trackingId) {
      return null;
    }

    // Use the centralized tracking status function
    const trackingStatus = getTrackingStatus(transaction, { shortFormat: true });
    
    if (!trackingStatus.needsTracking) {
      return null;
    }

    return (
      <div className={`tracking-time-remaining ${trackingStatus.colorClass}`}>
        <span className="time-icon">⏱️</span>
        <span>Tracking required: {trackingStatus.displayText} remaining</span>
      </div>
    );
  };

  // Determine if the transaction is disputed or has previous disputes
  const isDisputed = transaction?.isDisputed;
  const hasPreviousDisputes = transaction?.hasPreviousDisputes;

  console.log("Transaction isDisputed:", isDisputed);
  
  // Determine if the transaction can be disputed
  const canBeDisputed = transaction &&
    (transaction.status === 'in_escrow' || transaction.status === 'pending') &&
    !isDisputed;
  
  // Handle open dispute click
  const handleOpenDispute = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation to transaction details
    if (transaction && onOpenDispute) {
      onOpenDispute(transaction, e);
    }
  };
  
  return (
    <div
      className={`transaction-item ${isDisputed ? 'disputed' : ''} ${hasPreviousDisputes && !isDisputed ? 'previously-disputed' : ''} ${''}`}
      onClick={onClick}
    >
      {/* Add price tag if price exists and style is not 'none' */}
      {itemPrice && priceTagStyle !== 'none' && (
        <div className={`price-tag price-tag-${priceTagStyle}`}>
          {itemPrice}
        </div>
      )}
      <div className="transaction-avatar">
        <img 
          src={avatar} 
          alt={`${role} avatar`} 
        />
      </div>
      <div className="transaction-details">
        <div>
          <Text className="text-body">{itemDescription}</Text>
        </div>
        {/* Only show price as line item if priceTagStyle is 'none' */}
        {itemPrice && priceTagStyle === 'none' && (
          <div>
            <Text className="text-body">{itemPrice}</Text>
          </div>
        )}
        {itemBuyer && (
          <div>
            <LabeledValue className="text-body" label='Buyer' value={itemBuyer}/>
          </div>
        )}
        {itemSeller && (
          <div>
            <LabeledValue className="text-body" label='Seller' value={itemSeller}/>
          </div>
        )}
        <div>
            {isDisputed && (
            <div className="dispute-badge">
              DISPUTED
            </div>
            )}

            {!isDisputed && (
            typeof status === 'string' ? (
              <Text className="text-caption text-gray">{status}</Text>
            ) : (
              status
            )
            )}
        </div>
        
        {hasPreviousDisputes && !isDisputed && (
          <div className="previous-dispute-badge">
            PREVIOUSLY DISPUTED
          </div>
        )}
        
        {/* Add tracking time remaining indicator */}
        {transaction && renderTrackingTimeRemaining()}
        
        
        
        {action && (
          <div className="transaction-action">
            {action}
          </div>
        )}
      </div>
      
      <div className="transaction-arrow">
        <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" fill="none">
          <polyline points="9 18 15 12 9 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>
    </div>
  );
};

export default TransactionItem;